import type { ReactNode } from "react"
import {
	IconSquaresConnectedOutline18 as Connector,
	IconSidebarRightOutline18 as PanelRight,
} from "nucleo-ui-outline-18"

import {
	Badge,
	Button,
	IconButton,
	Input,
	LinearIcon,
	SelectTrigger,
} from "@productlane/ui"

import { ReportingKpiCard } from "@/components/reporting/charts"
import { isDevEnvironment } from "@/env"
import { RecommendationTypeIcon } from "@/screens/app/agent/recommendation-type-icon"
import { ThreadQuoteRow } from "@/screens/app/agent/thread-quote-row"
import { DetailTopBar } from "@/screens/app/page-header"
import { NotFound } from "@/screens/not-found"

// Fictional example only: this page ships in the prod bundle.
const EXAMPLE = {
	title: "Look up a customer's plan in Stripe",
	summary:
		"Customers ask which plan they are on and when it renews. The agent has no access to billing data, so it hands every one of these to your team.",
	need: "Read a customer's current plan, seats and renewal date.",
	suggestion:
		"A custom connector that looks up the Stripe customer by email and returns the active subscription.",
	questions: [
		"Which plan are we on right now?",
		"When does our subscription renew?",
		"How many seats do we pay for?",
	],
	identifier: "ENG-512",
	team: "Engineering",
}

const EVIDENCE = [
	{ quote: "Can you tell me which plan we're on?", label: "Handed off" },
	{ quote: "When is our next invoice due?", label: "Teammate answered" },
	{ quote: "How many seats are on our account?", label: "Handed off" },
]

const LIST = [
	{
		title: "Fix the SSO login loop in Safari",
		meta: "Code · Awaiting approval",
		draft: true,
	},
	{ title: EXAMPLE.title, meta: "Engineering · 4 conversations", impact: "+3" },
	{
		title: "Resend invoice emails on request",
		meta: "Engineering · 3 conversations",
		impact: "+2",
	},
]

function NewBadge({ children = "New" }: { children?: string }) {
	return (
		<Badge size="sm" variant="green" className="flex-none">
			{children}
		</Badge>
	)
}

function Section({
	crumb,
	note,
	children,
}: {
	crumb: string
	note?: string
	children: ReactNode
}) {
	return (
		<div className="space-y-3">
			<div className="text-xs font-medium text-tertiary">{crumb}</div>
			{note && (
				<div className="max-w-[704px] text-xxs text-tertiary">{note}</div>
			)}
			{children}
		</div>
	)
}

function Frame({ children }: { children: ReactNode }) {
	return (
		<div className="w-[760px] overflow-hidden rounded-[10px] bg-white shadow-sm ring-1 ring-black/5 dark:bg-gray-850 dark:ring-white/10">
			{children}
		</div>
	)
}

function CodeTabList() {
	return (
		<Frame>
			<div className="flex gap-1 px-4 pb-2 pt-3">
				{["All", "Chats", "Docs", "Guidance", "Code"].map((tab) => (
					<Button
						key={tab}
						size="sm"
						variant={tab === "Code" ? "default" : "outline-subtle"}
						className="px-2"
					>
						{tab}
					</Button>
				))}
			</div>
			<ul className="flex list-none flex-col gap-px px-2 pb-3">
				{LIST.map((row, i) => (
					<li
						key={row.title}
						className={
							i === 1
								? "flex items-center gap-3 rounded-lg bg-gray-150 px-3 py-2.5 dark:bg-gray-850"
								: "flex items-center gap-3 rounded-lg px-3 py-2.5"
						}
					>
						{row.draft ? (
							<RecommendationTypeIcon type="CODE" />
						) : (
							<Connector className="h-4 w-4 flex-none text-secondary" />
						)}
						<div className="flex min-w-0 flex-1 flex-col gap-0.5">
							<span className="flex items-center gap-2 truncate text-sm font-medium">
								{row.title}
								{!row.draft && <NewBadge />}
							</span>
							<span className="truncate text-xs text-tertiary">{row.meta}</span>
						</div>
						{row.draft ? (
							<Badge variant="amber" size="sm">
								Draft
							</Badge>
						) : (
							<span className="flex-none whitespace-nowrap text-xs font-medium tabular-nums">
								{row.impact}{" "}
								<span className="font-normal text-tertiary">/mo</span>
							</span>
						)}
					</li>
				))}
			</ul>
		</Frame>
	)
}

function Detail({ created }: { created: boolean }) {
	return (
		<Frame>
			<DetailTopBar className="pl-7 pr-4">
				<div className="flex min-w-0 flex-1 items-center gap-2 text-sm text-secondary">
					<Connector className="h-3.5 w-3.5 flex-none" />
					Custom connectors
				</div>
				<div className="flex flex-none items-center gap-2">
					{created ? (
						<>
							<Badge variant="stat">
								<LinearIcon className="h-3 w-3 flex-none" />
								{EXAMPLE.identifier} · Todo
							</Badge>
							<Button variant="outline" Icon={LinearIcon}>
								Open in Linear
							</Button>
						</>
					) : (
						<>
							<Button variant="ghost">Dismiss</Button>
							<Button Icon={LinearIcon}>Accept and create issue</Button>
						</>
					)}
					<IconButton
						variant="ghost"
						Icon={PanelRight}
						aria-label="AI Assistant"
					/>
				</div>
			</DetailTopBar>
			<div className="mx-auto flex w-full max-w-[680px] flex-col gap-10 px-8 pb-12 pt-10">
				<header className="flex flex-col gap-4">
					<div className="flex items-center gap-1.5">
						<Badge>Engineering</Badge>
						<NewBadge>New type</NewBadge>
					</div>
					<h2 className="text-balance text-[26px] font-semibold leading-tight tracking-tight">
						{EXAMPLE.title}
					</h2>
					<p className="text-base leading-relaxed text-secondary">
						{EXAMPLE.summary}
					</p>
					<div className="grid grid-cols-3 gap-2.5">
						<ReportingKpiCard label="Extra resolutions a month" value="+3" />
						<ReportingKpiCard label="Conversations" value="4" />
						<ReportingKpiCard label="Customers" value="4" />
					</div>
				</header>
				<section className="flex flex-col gap-2 border-t border-gray-200 pt-8 dark:border-gray-700/80">
					<h3 className="text-[15px] font-semibold tracking-tight">
						What customers asked
					</h3>
					<ul className="flex list-none flex-col gap-px">
						{EVIDENCE.map((item) => (
							<li key={item.quote}>
								<ThreadQuoteRow to="#" quote={item.quote} label={item.label} />
							</li>
						))}
					</ul>
					<p className="text-xs text-tertiary">and 1 more</p>
				</section>
				<section className="flex flex-col gap-3 border-t border-gray-200 pt-8 dark:border-gray-700/80">
					<h3 className="text-[15px] font-semibold tracking-tight">
						What the agent needs
					</h3>
					<p className="text-[15px] leading-relaxed">{EXAMPLE.need}</p>
					<p className="text-[15px] leading-relaxed text-secondary">
						{EXAMPLE.suggestion}
					</p>
					<div className="flex flex-col gap-1">
						<span className="text-xs text-tertiary">
							It could then answer questions like
						</span>
						{EXAMPLE.questions.map((q) => (
							<span key={q} className="text-sm text-secondary">
								{`"${q}"`}
							</span>
						))}
					</div>
				</section>
			</div>
		</Frame>
	)
}

function CreateIssuePanel() {
	return (
		<div className="w-[360px] space-y-3 rounded-xl bg-white p-4 shadow-lg ring-1 ring-black/5 dark:bg-gray-850 dark:ring-white/10">
			<div className="flex items-center gap-2 text-sm font-medium text-primary">
				<LinearIcon className="h-4 w-4 flex-none" />
				New Linear issue
			</div>
			<SelectTrigger className="w-full">{EXAMPLE.team}</SelectTrigger>
			<Input
				name="issue-title"
				inputClassName="h-9 rounded-lg"
				defaultValue="Agent: look up a customer's plan in Stripe"
			/>
			<p className="text-xs leading-relaxed text-tertiary">
				The description carries what the agent needs, the suggested connector
				and the example questions. The 4 conversations are linked as customer
				requests.
			</p>
			<div className="flex justify-end gap-2">
				<Button variant="ghost">Cancel</Button>
				<Button>Create issue</Button>
			</div>
		</div>
	)
}

export function AgentLinearIssuePage() {
	if (!isDevEnvironment) return <NotFound />
	return (
		<div className="h-full overflow-y-auto bg-gray-50 dark:bg-gray-900">
			<div className="mx-auto max-w-[1100px] space-y-12 px-10 pb-24 pt-10">
				<div className="space-y-2">
					<h1 className="text-lg font-semibold text-primary">
						Agent: send engineering work to Linear
					</h1>
					<p className="max-w-[704px] text-sm text-secondary">
						When the agent needs something only engineers can build, like a
						custom connector, the recommendation becomes a Linear issue. The
						customers who asked are linked to it, so loop closing tells them
						when it ships.
					</p>
				</div>
				<Section
					crumb="Agent / Code"
					note="Engineering recommendations sit under Code, next to Bug Inspect fixes."
				>
					<CodeTabList />
				</Section>
				<Section
					crumb="Agent / Recommendation"
					note="Accept and create issue replaces Accept for engineering work."
				>
					<Detail created={false} />
				</Section>
				<Section
					crumb="Accept and create issue"
					note="Opens under the button. The team defaults to the one picked in Linear settings."
				>
					<CreateIssuePanel />
				</Section>
				<Section
					crumb="After creating"
					note="The recommendation leaves the open list and shows the issue and its status."
				>
					<Detail created />
				</Section>
			</div>
		</div>
	)
}
